
import React, { useEffect, useState, useCallback } from "react";
import {
  Search,
  Download,
  Printer,
  FileSpreadsheet,
  FileText,
  Info,
} from "lucide-react";
import { FaCheckCircle } from "react-icons/fa";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import Apiurl from "../environment/Apiurl";

// Utility function to escape CSV values
const escapeCSVValue = (value) => {
  if (value == null) return "";
  const str = String(value);
  if (str.includes(",") || str.includes('"') || str.includes("\n")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

// Utility function to parse amount safely
const parseAmount = (amount) => {
  if (!amount || typeof amount === "number") return amount || 0;
  const cleaned = amount.replace(/[₹,]/g, "");
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
};

const Reports = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const active = location.state?.active || "billing";
  const [activeTab, setActiveTab] = useState(active);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [doctorFilter, setDoctorFilter] = useState("All");
  const [paymentFilter, setPaymentFilter] = useState("All");
  const [serviceFilter, setServiceFilter] = useState("All"); // New state for service charge/type filter
  const [selectedStatusFilter, setSelectedStatusFilter] = useState("All");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [error, setError] = useState(null);

  // State for data
  const [patientDetailsData, setPatientDetailsData] = useState([]);
  const [doctorListData, setDoctorListData] = useState([]);
  const [staffDetailsData, setStaffDetailsData] = useState([]);
  const [billingManagementData, setBillingManagementData] = useState([]);
  const [Outbilling, setOutbilling ] = useState([]);
  const [expenseListData, setExpenseListData] = useState([]);
  const [appointmentData, setAppointmentData] = useState([]);
  const [maintenance, setMaintenance] = useState([]);
  const [stock, setStock] = useState([]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [labData, setLabData] = useState([]);

  // Fetch data for the active tab only
  const fetchDataForTab = useCallback(async (tab) => {
    try {
      setError(null);
      let response;
      switch (tab) {
        case "patients":
          response = await axios.get(`${Apiurl}/patient_details`);
          setPatientDetailsData(response.data || []);
          break;
        case "doctors":
          response = await axios.get(`${Apiurl}/doctor`);
          setDoctorListData(response.data || []);
          break;
        case "staff":
          response = await axios.get(`${Apiurl}/staff`);
          setStaffDetailsData(response.data || []);
          break;
        case "billing":
          response = await axios.get(`${Apiurl}/billing_management_list`);
          setBillingManagementData(response.data || []);
          break;
        case "overallBilling":
          response = await axios.get(`${Apiurl}/billing_management`);
          setOutbilling(response.data || []);
          break;
        case "expenses":
          response = await axios.get(`${Apiurl}/expense`);
          setExpenseListData(response.data || []);
          break;
        case "appointment":
          response = await axios.get(`${Apiurl}/appointments`);
          setAppointmentData(response.data || []);
          break;
        case "maintenance":
          response = await axios.get(`${Apiurl}/maintenance`);
          setMaintenance(response.data || []);
          break;
        case "pharmacy":
          response = await axios.get(`${Apiurl}/stock_pharmacy`);
          setStock(response.data || []);
          break;
        case "attendance":
          response = await axios.get(`${Apiurl}/attendance`);
          const uniqueAttendance = response.data.reduce((acc, current) => {
            const existing = acc.find(
              (item) =>
                item.date === current.date && item.staff_id === current.staff_id
            );
            if (!existing) acc.push(current);
            return acc;
          }, []);
          setAttendanceData(uniqueAttendance || []);
          break;
        case "lab":
          response = await axios.get(`${Apiurl}/lab_management`);
          setLabData(response.data || []);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error(`Error fetching ${tab} data:`, error.message);
      setError(`Failed to fetch ${tab} data. Please try again.`);
    }
  }, []);

  // Fetch doctor and staff data for filters
  useEffect(() => {
    fetchDataForTab("doctors");
    fetchDataForTab("staff");
  }, [fetchDataForTab]);

  // Fetch data when activeTab changes
  useEffect(() => {
    fetchDataForTab(activeTab);
  }, [activeTab, fetchDataForTab]);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Utility functions
  const formatDate = (dateStr) => {
    if (!dateStr || dateStr === "N/A" || dateStr === "") return "N/A";
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  };

  const normalizeTime = (time) => {
    if (!time || time === "N/A" || time === "") return "N/A";
    if (time.includes("AM") || time.includes("PM")) return time;
    const [hours, minutes] = time.split(":");
    const hoursNum = parseInt(hours, 10);
    const period = hoursNum >= 12 ? "PM" : "AM";
    const normalizedHours = hoursNum % 12 || 12;
    return `${normalizedHours}:${minutes} ${period}`;
  };

  // Tab configuration
  const tabConfig = {
    patients: {
      title: "Patients List",
      headers: [
        "S.No",
        "Patient ID",
        "Patient Type",
        "Name",
        "Gender",
        "Age",
        "Contact",
        "Purpose",
        "Status",
      ],
      fields: [
        "sNo",
        "patient_id",
        "patient_type",
        "fullName",
        "gender",
        "age",
        "mobile_no",
        "purpose",
        "status",
      ],
      data: patientDetailsData,
    },
    doctors: {
      title: "Doctors List",
      headers: [
        "S.No",
        "Doctor ID",
        "Name",
        "Specialization",
        "Contact",
        "Email",
        "Status",
      ],
      fields: [
        "sNo",
        "doctor_id",
        "doctor_name",
        "speciality",
        "mobile_no",
        "email",
        "status",
      ],
      data: doctorListData,
    },
    staff: {
      title: "Staff List",
      headers: [
        "S.No",
        "Staff ID",
        "Name",
        "Department",
        "Role",
        "Contact",
        "Email",
        "Status",
      ],
      fields: [
        "sNo",
        "staff_id",
        "fullName",
        "department",
        "designation_name",
        "mobile_no",
        "email",
        "status",
      ],
      data: staffDetailsData,
    },
    billing: {
      title: "Billing Records",
      headers: [
        "S.No",
        "Bill No",
        "Patient ID",
        "Patient Name",
        "Category",
        "Patient Type",
        "Service Charge",
        "Date",
        "Payment Mode",
        "Status",
        "Amount",
      ],
      fields: [
        "sNo",
        "bill_invoice_no",
        "patient_id",
        "Patient_Name",
        "Category",
        "department",
        "service_type",
        "bill_date",
        "payment_type",
        "Status",
        "fees",
      ],
      data: billingManagementData,
    },
    overallBilling: {
      title: "Overall Outbilling",
      headers: [
        "S.No",
        "Invoice No",
        "Patient ID",
        "Bill Date",
        "Patients Name",
        "Doctor",
        "Paytype",
        "Patient Type",
        "Bill Amount",
      ],
      fields: [
        "sNo",
        "bill_invoice_no",
        "patient_id",
        "bill_date",
        "patients_name",
        "doctor_name",
        "payment_type",
        "patient_type",
        "total_amount",
      ],
      data: Outbilling,
    },
    expenses: {
      title: "Expenses Records",
      headers: [
        "S.No",
        "Category",
        "Description",
        "Amount",
        "Date",
        "Approved By",
        "Status",
      ],
      fields: [
        "sNo",
        "type",
        "description",
        "amount",
        "date",
        "addedBy",
        "status",
      ],
      data: expenseListData,
    },
    maintenance: {
      title: "Maintenance Records",
      headers: [
        "S.No",
        "Maintenance ID",
        "Maintenance Date",
        "Department",
        "Maintenance Type",
        "Area Name",
        "Vendor",
        "Amount",
        "Status",
      ],
      fields: [
        "sNo",
        "maintenance_id",
        "maintenance_Date",
        "department",
        "maintenance_type",
        "area_name",
        "vendor",
        "amount",
        "Status",
      ],
      data: maintenance,
    },
    pharmacy: {
      title: "Pharmacy Inventory",
      headers: [
        "S.No",
        "Medicine Name",
        "Batch No",
        "Expiry Date",
        "Category",
        "Inward Qty",
        "Outward Qty",
        "Balance Qty",
        "MRP",
        "Status",
      ],
      fields: [
        "sNo",
        "medicine_name",
        "batch_no",
        "expiry_date",
        "category",
        "inward_quantity",
        "outward_quantity",
        "balance_quantity",
        "mrp",
        "status",
      ],
      data: stock,
    },
    lab: {
      title: "Lab Reports",
      headers: [
        "S.No",
        "Lab Report ID",
        "Lab Source",
        "Patient ID",
        "Lab Name",
        "Date",
        "Result",
        "Amount",
        "Status",
      ],
      fields: [
        "sNo",
        "lab_report_id",
        "lab_source",
        "patient_id",
        "lab_name",
        "report_date",
        "result_values",
        "charge_amount",
        "payment_status",
      ],
      data: labData,
    },
    appointment: {
      title: "Appointments",
      headers: [
        "S.No",
        "Appointment ID",
        "Patient Name",
        "Patient Type",
        "Doctor Name",
        "Date",
        "Time",
        "Status",
      ],
      fields: [
        "sNo",
        "appointment_id",
        "patient_name",
        "department",
        "doctor_name",
        "appointment_date",
        "appointment_time",
        "appointment_status",
      ],
      data: appointmentData,
    },
    attendance: {
      title: "Attendance",
      headers: [
        "S.No",
        "Staff ID",
        "Staff Name",
        "Date",
        "Login",
        "Logout",
        "Status",
        "View",
      ],
      fields: [
        "sNo",
        "staff_id",
        "first_name",
        "date",
        "login",
        "logout",
        "status",
        "view",
      ],
      data: attendanceData,
    },
  };

  // Process data for the current tab
  const getCurrentData = () => {
    const config = tabConfig[activeTab];
    if (!config) return [];

    let rawData = config.data || [];

    // For overallBilling, filter only category "out"
    if (activeTab === "overallBilling") {
      rawData = rawData.filter((item) => item.category === "out");
    }

    return rawData.map((item, index) => {
      const row = { sNo: index + 1 };
      config.fields.forEach((field) => {
        if (field === "sNo") {
          row[field] = index + 1;
        } else if (field === "fullName" && activeTab === "patients") {
          row[field] = `${item.first_name || ""} ${item.last_name || ""}`.trim() || "N/A";
        } else if (field === "fullName" && activeTab === "staff") {
          row[field] = `${item.first_name || ""} ${item.last_name || ""}`.trim() || "N/A";
        } else if (field === "patient_name" && activeTab === "appointment") {
          const patient = item.patients_details?.[0];
          row[field] = patient
            ? `${patient.first_name || ""} ${patient.last_name || ""}`.trim() || "N/A"
            : "N/A";
        } else if (field === "doctor_name" && activeTab === "appointment") {
          row[field] = item.doctor_details?.[0]?.doctor_name || "N/A";
        } else if (field === "Patient_Name" && activeTab === "billing") {
          row[field] = item.Patient_Name || item.patients_details?.[0]?.first_name || "N/A";
        } else if (activeTab === "overallBilling") {
          if (field === "patients_name") {
            const patient = item.patients_details?.[0];
            row[field] = patient ? `${patient.first_name || ""} ${patient.last_name || ""}`.trim() || "N/A" : "N/A";
          } else if (field === "doctor_name") {
            row[field] = item.doctor_details?.[0]?.doctor_name || "N/A";
          } else if (field === "patient_type") {
            const patient = item.patients_details?.[0];
            row[field] = patient?.patient_type || "N/A";
          } else {
            row[field] = item[field] || "N/A";
          }
        } else if (
          field === "Amount" &&
          (activeTab === "billing" || activeTab === "expenses" || activeTab === "maintenance")
        ) {
          const amount = parseAmount(item[field] || item["amount"]);
          row[field] = amount ? `₹${amount.toFixed(2)}` : "N/A";
        } else if (field === "mrp" && activeTab === "pharmacy") {
          const amount = parseAmount(item[field]);
          row[field] = amount ? `₹${amount.toFixed(2)}` : "N/A";
        } else if (field === "Status" && activeTab === "billing") {
          row[field] = item.Status || (item.balance === "0.00" ? "Paid" : "Pending");
        } else if (
          (field === "login" || field === "logout") &&
          activeTab === "attendance"
        ) {
          row[field] = normalizeTime(item[field]);
        } else if (field === "status" && activeTab === "attendance") {
          row[field] = item[field] || "Present";
        } else if (field === "status" && activeTab === "pharmacy") {
          const balance = parseInt(item.balance_quantity, 10) || 0;
          row[field] =
            balance > 10 ? "Available" : balance > 0 ? "Low Stock" : "Out of Stock";
        } else if (field === "view" && activeTab === "attendance") {
          row[field] = <Info size={16} className="text-primary" />;
        } else if (field === "payment_status" && activeTab === "lab") {
          row[field] = item[field] === "Completed" ? "Completed" : "Pending";
        } else if (
          [
            "bill_date",
            "date",
            "appointment_date",
            "report_date",
            "expiry_date",
            "maintenance_Date",
          ].includes(field)
        ) {
          row[`original_${field}`] = item[field];
          row[field] = formatDate(item[field]);
        } else if (field === "medicine_name" && activeTab === "pharmacy") {
          row[field] =
            item.medicine_details?.medicine_name || item.medicine_name || "N/A";
        } else if (field === "category" && activeTab === "pharmacy") {
          row[field] = item.medicine_details?.category || "N/A";
        } else {
          row[field] = item[field] || "N/A";
        }
      });
      return row;
    });
  };

  // Apply filters
  const getFilteredData = () => {
    let currentData = getCurrentData();

    // Category/Patient Type/Department/Doctor filter
    if (categoryFilter !== "All") {
      if (activeTab === "patients") {
        currentData = currentData.filter(
          (item) => item.patient_type?.toLowerCase() === categoryFilter.toLowerCase()
        );
      } else if (activeTab === "billing") {
        currentData = currentData.filter(
          (item) => item.department?.toLowerCase() === categoryFilter.toLowerCase()
        );
      } else if (activeTab === "appointment") {
        currentData = currentData.filter(
          (item) => item.department?.toLowerCase() === categoryFilter.toLowerCase()
        );
      } else if (activeTab === "attendance") {
        currentData = currentData.filter((item) => item.staff_id === categoryFilter);
      } else if (activeTab === "expenses") {
        currentData = currentData.filter(
          (item) => item.type?.toLowerCase() === categoryFilter.toLowerCase()
        );
      } else if (activeTab === "pharmacy") {
        currentData = currentData.filter(
          (item) => item.category?.toLowerCase() === categoryFilter.toLowerCase()
        );
      }
    }

    // Doctor filter for appointments
    if (doctorFilter !== "All" && activeTab === "appointment") {
      currentData = currentData.filter((item) => item.doctor_name === doctorFilter);
    }

    // Doctor filter for patients
    if (doctorFilter !== "All" && activeTab === "patients") {
      const associatedPatientIds = new Set(
        appointmentData
          .filter((app) => app.doctor_details?.[0]?.doctor_name === doctorFilter)
          .flatMap((app) => app.patients_details?.map((pd) => pd.patient_id) || [])
          .filter((id) => id)
      );
      currentData = currentData.filter((patient) =>
        associatedPatientIds.has(patient.patient_id)
      );
    }

    // Payment Type filter for billing
    if (paymentFilter !== "All" && activeTab === "billing") {
      currentData = currentData.filter(
        (item) => item.payment_type?.toLowerCase() === paymentFilter.toLowerCase()
      );
    }

    // Service Type filter for billing
    if (serviceFilter !== "All" && activeTab === "billing") {
      currentData = currentData.filter(
        (item) => item.service_type?.toLowerCase() === serviceFilter.toLowerCase()
      );
    }

    // Date range filter
    if (fromDate || toDate) {
      currentData = currentData.filter((item) => {
        const dateFieldMap = {
          billing: "bill_date",
          appointment: "appointment_date",
          maintenance: "maintenance_Date",
          pharmacy: "expiry_date",
          expenses: "date",
          lab: "report_date",
          attendance: "date",
          overallBilling: "bill_date",
        };
        const dateField = dateFieldMap[activeTab] || "date";
        const itemDateStr = item[`original_${dateField}`] || item[dateField];
        const itemDate = new Date(itemDateStr);
        if (isNaN(itemDate.getTime())) return false;

        const from = fromDate ? new Date(fromDate) : null;
        const to = toDate ? new Date(toDate) : null;
        if (to) to.setHours(23, 59, 59, 999); // Include full toDate

        return (!from || itemDate >= from) && (!to || itemDate <= to);
      });
    }

    // Status filter for appointments and pharmacy
    if (
      (activeTab === "appointment" || activeTab === "pharmacy") &&
      selectedStatusFilter !== "All"
    ) {
      currentData = currentData.filter((item) => {
        if (activeTab === "appointment") {
          if (selectedStatusFilter === "Completed") {
            return item.appointment_status === "Completed";
          }
          const appDateStr = item.original_appointment_date || item.appointment_date;
          const appDate = new Date(appDateStr);
          if (isNaN(appDate.getTime())) return false;
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          appDate.setHours(0, 0, 0, 0);
          switch (selectedStatusFilter) {
            case "Today":
              return appDate.toDateString() === today.toDateString();
            case "Upcoming":
              return appDate > today;
            default:
              return true;
          }
        } else if (activeTab === "pharmacy") {
          return item.status === selectedStatusFilter;
        }
        return true;
      });
    }

    // Search filter
    if (searchTerm) {
      currentData = currentData.filter((item) =>
        Object.values(item)
          .filter((value) => typeof value === "string")
          .some((value) => value.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    return currentData;
  };

  // Pagination
  const getPaginatedData = () => {
    const filteredData = getFilteredData();
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(startIndex, startIndex + itemsPerPage);
  };

  const getTotalPages = () => {
    const filteredDataLength = getFilteredData().length;
    return Math.ceil(filteredDataLength / itemsPerPage) || 1;
  };

  // Export functions
  const downloadCSV = () => {
    const currentData = getFilteredData();
    const config = tabConfig[activeTab];
    const headers = config.headers;
    const fields = config.fields;

    let csvContent = headers.map(escapeCSVValue).join(",") + "\n";
    currentData.forEach((item) => {
      const row = fields
        .map((field) => escapeCSVValue(field === "view" ? "" : item[field] || ""))
        .join(",");
      csvContent += row + "\n";
    });

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${activeTab}_report.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const downloadExcel = () => {
    const currentData = getFilteredData();
    const config = tabConfig[activeTab];
    const headers = config.headers;
    const fields = config.fields;

    let excelContent = '<table border="1">';
    excelContent +=
      "<tr>" + headers.map((h) => `<th>${h}</th>`).join("") + "</tr>";

    currentData.forEach((item) => {
      excelContent +=
        "<tr>" +
        fields
          .map((field) =>
            field === "view" ? "<td></td>" : `<td>${item[field] || ""}</td>`
          )
          .join("") +
        "</tr>";
    });

    excelContent += "</table>";

    const blob = new Blob([excelContent], {
      type: "application/vnd.ms-excel",
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${activeTab}_report.xls`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const downloadPDF = () => {
    const currentData = getFilteredData();
    const config = tabConfig[activeTab];
    const headers = config.headers;
    const fields = config.fields;

    const doc = new jsPDF();

    doc.setFontSize(18);
    doc.text(`${config.title} Report`, 14, 20);

    doc.setFontSize(12);
    doc.text(`Generated on: ${formatDate(new Date())}`, 14, 30);

    if (fromDate || toDate) {
      const dateRangeText = `Date Range: ${fromDate ? formatDate(fromDate) : "N/A"} to ${toDate ? formatDate(toDate) : "N/A"}`;
      doc.text(dateRangeText, 14, 35);
    }

    const tableData = currentData.map((item) =>
      fields.map((field) => (field === "view" ? "" : item[field] || ""))
    );

    let totalAmount = 0;
    if (["billing", "expenses", "maintenance", "pharmacy", "overallBilling"].includes(activeTab)) {
      totalAmount = currentData.reduce((sum, item) => {
        const amountField = activeTab === "pharmacy" ? "mrp" : activeTab === "overallBilling" ? "total_amount" : "Amount" in item ? "Amount" : "amount";
        return sum + parseAmount(item[amountField]);
      }, 0);
    }

    autoTable(doc, {
      startY: fromDate || toDate ? 45 : 40,
      head: [headers],
      body: tableData,
      theme: "striped",
      headStyles: {
        fillColor: [46, 146, 144],
        textColor: [255, 255, 255],
        fontSize: 10,
      },
      bodyStyles: {
        fontSize: 9,
      },
      alternateRowStyles: {
        fillColor: [242, 242, 242],
      },
      margin: {
        top: fromDate || toDate ? 45 : 40,
      },
    });

    if (["billing", "expenses", "maintenance", "pharmacy", "overallBilling"].includes(activeTab)) {
      const finalY = doc.lastAutoTable.finalY || 40;
      doc.setFontSize(12);
      doc.text(`Total Amount: ₹${totalAmount.toFixed(2)}`, 14, finalY + 10);
    }

    doc.save(`${activeTab}_report.pdf`);
  };

  const handlePrint = () => {
    const currentData = getFilteredData();
    const config = tabConfig[activeTab];
    const headers = config.headers;
    const fields = config.fields;

    let totalAmount = 0;
    if (["billing", "expenses", "maintenance", "pharmacy", "overallBilling"].includes(activeTab)) {
      totalAmount = currentData.reduce((sum, item) => {
        const amountField = activeTab === "pharmacy" ? "mrp" : activeTab === "overallBilling" ? "total_amount" : "Amount" in item ? "Amount" : "amount";
        return sum + parseAmount(item[amountField]);
      }, 0);
    }

    const printWindow = window.open("", "_blank");
    let htmlContent = `
      <html>
        <head>
          <title>${config.title} Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #01AEBE; text-align: center; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #01AEBE; color: white; }
            tr:nth-child(even) { background-color: #f2f2f2; }
            .total-amount { margin-top: 20px; font-weight: bold; }
            .date-range { margin-top: 10px; }
            @media print {
              body { margin: 0; }
              h1 { margin-bottom: 20px; }
            }
          </style>
        </head>
        <body>
          <h1>${config.title} Report</h1>
          <p>Generated on: ${formatDate(new Date())}</p>
    `;

    if (fromDate || toDate) {
      htmlContent += `<p class="date-range">Date Range: ${fromDate ? formatDate(fromDate) : "N/A"} to ${toDate ? formatDate(toDate) : "N/A"}</p>`;
    }

    htmlContent += `
          <table>
            <thead>
              <tr>${headers.map((h) => `<th>${h}</th>`).join("")}</tr>
            </thead>
            <tbody>
              ${currentData
        .map(
          (item) =>
            `<tr>${fields
              .map((field) =>
                field === "view"
                  ? "<td></td>"
                  : `<td>${item[field] || ""}</td>`
              )
              .join("")}</tr>`
        )
        .join("")}
            </tbody>
          </table>
    `;

    if (["billing", "expenses", "maintenance", "pharmacy", "overallBilling"].includes(activeTab)) {
      htmlContent += `<div class="total-amount">Total Amount: ₹${totalAmount.toFixed(2)}</div>`;
    }

    htmlContent += `
        </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  // Status badge styling
  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      Active: "badge rounded-pill bg-success-subtle text-success fw-semibold px-3 py-2",
      Admitted: "badge rounded-pill bg-success-subtle text-success fw-semibold px-3 py-2",
      Visited: "badge rounded-pill bg-success-subtle text-success fw-semibold px-3 py-2",
      Treated: "badge rounded-pill bg-success-subtle text-success fw-semibold px-3 py-2",
      Discharged: "badge rounded-pill bg-success-subtle text-success fw-semibold px-3 py-2",
      "On Leave": "badge rounded-pill bg-warning-subtle text-warning fw-semibold px-3 py-2",
      Paid: "badge rounded-pill bg-success-subtle text-success fw-semibold px-3 py-2",
      Pending: "badge rounded-pill bg-warning-subtle text-warning fw-semibold px-3 py-2",
      Approved: "badge rounded-pill bg-success-subtle text-success fw-semibold px-3 py-2",
      Completed: "badge rounded-pill bg-success-subtle text-success fw-semibold px-3 py-2",
      "In Progress": "badge rounded-pill bg-warning-subtle text-warning fw-semibold px-3 py-2",
      Scheduled: "badge rounded-pill bg-secondary-subtle text-secondary fw-semibold px-3 py-2",
      Available: "badge rounded-pill bg-success-subtle text-success fw-semibold px-3 py-2",
      "Low Stock": "badge rounded-pill bg-warning-subtle text-warning fw-semibold px-3 py-2",
      "Out of Stock": "badge rounded-pill bg-danger-subtle text-danger fw-semibold px-3 py-2",
      Normal: "badge rounded-pill bg-success-subtle text-success fw-semibold px-3 py-2",
      Abnormal: "badge rounded-pill bg-danger-subtle text-danger fw-semibold px-3 py-2",
      Confirmed: "badge rounded-pill bg-success-subtle text-success fw-semibold px-3 py-2",
      Cancelled: "badge rounded-pill bg-danger-subtle text-danger fw-semibold px-3 py-2",
      Present: "badge rounded-pill bg-success-subtle text-success fw-semibold px-3 py-2",
    };
    return (
      statusClasses[status] ||
      "badge rounded-pill bg-warning-subtle text-warning fw-semibold px-3 py-2"
    );
  };

  // Dynamic filter options
  const getCategoryOptions = () => {
    const data = tabConfig[activeTab]?.data || [];
    const field = activeTab === "patients" ? "patient_type" : activeTab === "pharmacy" ? "category" : "department";
    const uniqueCategories = [...new Set(data.map((item) => item[field]?.toLowerCase()).filter(Boolean))];
    return uniqueCategories.map((cat) => ({
      value: cat,
      label: cat.charAt(0).toUpperCase() + cat.slice(1),
    }));
  };

  const getServiceOptions = () => {
    const data = tabConfig["billing"]?.data || [];
    const uniqueServices = [...new Set(data.map((item) => item.service_type?.toLowerCase()).filter(Boolean))];
    return uniqueServices.map((service) => ({
      value: service,
      label: service.charAt(0).toUpperCase() + service.slice(1),
    }));
  };

  // Render table
  const renderTable = () => {
    const paginatedData = getPaginatedData();
    const config = tabConfig[activeTab];

    let pageTotalAmount = 0;
    if (["billing", "expenses", "maintenance", "pharmacy", "overallBilling"].includes(activeTab)) {
      pageTotalAmount = paginatedData.reduce((sum, item) => {
        const amountField = activeTab === "pharmacy" ? "mrp" : activeTab === "overallBilling" ? "total_amount" : "fees";
        return sum + parseAmount(item[amountField]);
      }, 0);
    }

    return (
      <div className="table-responsive">
        <table className="table table-bordered">
          <thead>
            <tr style={{ backgroundColor: "#01AEBE", color: "white" }}>
              {config.headers.map((header, index) => (
                <th key={index} className="border-0">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.length === 0 ? (
              <tr>
                <td colSpan={config.headers.length} className="text-center">
                  No data available
                </td>
              </tr>
            ) : (
              paginatedData.map((item, index) => (
                <tr key={index} className="align-middle">
                  {config.fields.map((field, fieldIndex) => (
                    <td key={fieldIndex} className="border-0">
                      {field === "status" ||
                        field === "appointment_status" ||
                        (field === "payment_status" && activeTab === "lab") ||
                        (field === "Status" && activeTab === "billing") ? (
                        <span
                          className={`badge d-inline-flex align-items-center gap-1 ${getStatusBadgeClass(
                            item[field]
                          )}`}
                          style={{ borderRadius: "1rem" }}
                        >
                          <FaCheckCircle />
                          {item[field]}
                        </span>
                      ) : field === "view" && activeTab === "attendance" ? (
                        <button
                          className="btn btn-sm text-primary"
                          onClick={() =>
                            navigate(
                              `/report/report-view-attendance/${item.staff_id}`
                            )
                          }
                        >
                          <Info size={16} />
                        </button>
                      ) : (
                        item[field]
                      )}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
        {["billing", "expenses", "maintenance", "pharmacy", "overallBilling"].includes(activeTab) && (
          <div className="mt-2 text-end me-3 fw-bold">
            Total Amount: ₹{pageTotalAmount.toFixed(2)}
          </div>
        )}
      </div>
    );
  };

  // Render pagination
  const renderPagination = () => {
    const totalPages = getTotalPages();
    if (totalPages <= 1 && itemsPerPage !== Number.MAX_SAFE_INTEGER) return null;

    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="text-muted">
          Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
          {Math.min(currentPage * itemsPerPage, getFilteredData().length)} of{" "}
          {getFilteredData().length} entries
        </div>
        {itemsPerPage !== Number.MAX_SAFE_INTEGER && (
          <div className="d-flex gap-2">
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            {pages.map((page) => (
              <button
                key={page}
                className={`btn btn-sm ${currentPage === page ? "btn-success" : "btn-outline-secondary"
                  }`}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </button>
            ))}
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className="container-fluid p-4"
      style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}
    >
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}
      <div className="mb-4">
        <ul className="nav nav-tabs flex-wrap">
          {Object.entries(tabConfig).map(([key, config]) => (
            <li key={key} className="nav-item">
              <button
                className={`nav-link ${activeTab === key ? "active" : ""} px-3 py-2`}
                style={{
                  backgroundColor: activeTab === key ? "#01AEBE" : "transparent",
                  color: activeTab === key ? "white" : "#01AEBE",
                  border: "1px solid #01AEBE",
                  borderRadius: "0",
                  fontSize: "14px",
                }}
                onClick={() => {
                  setActiveTab(key);
                  setCurrentPage(1);
                  setSearchTerm("");
                  setCategoryFilter("All");
                  setDoctorFilter("All");
                  setPaymentFilter("All");
                  setServiceFilter("All");
                  setSelectedStatusFilter("All");
                  setFromDate("");
                  setToDate("");
                  setError(null);
                }}
              >
                {config.title}
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div className="card card-body mt-2">
        <div
          className="card-header d-flex justify-content-between align-items-center flex-wrap"
          style={{ backgroundColor: "#01AEBE", color: "white" }}
        >
          <h5 className="mb-0">{tabConfig[activeTab]?.title}</h5>

          {(activeTab === "appointment" || activeTab === "pharmacy") && (
            <div className="btn-group w-auto text-white bg-white crm-filter-tabs" role="group">
              <button
                className={`btn btn-outline-secondary ${selectedStatusFilter === "All" ? "active" : ""
                  }`}
                onClick={() => {
                  setSelectedStatusFilter("All");
                  setCurrentPage(1);
                }}
              >
                All
              </button>
              {activeTab === "appointment" ? (
                <>
                  <button
                    className={`btn btn-outline-secondary ${selectedStatusFilter === "Today" ? "active" : ""
                      }`}
                    onClick={() => {
                      setSelectedStatusFilter("Today");
                      setCurrentPage(1);
                    }}
                  >
                    Today
                  </button>
                  <button
                    className={`btn btn-outline-secondary ${selectedStatusFilter === "Upcoming" ? "active" : ""
                      }`}
                    onClick={() => {
                      setSelectedStatusFilter("Upcoming");
                      setCurrentPage(1);
                    }}
                  >
                    Upcoming
                  </button>
                  <button
                    className={`btn btn-outline-secondary ${selectedStatusFilter === "Completed" ? "active" : ""
                      }`}
                    onClick={() => {
                      setSelectedStatusFilter("Completed");
                      setCurrentPage(1);
                    }}
                  >
                    Completed
                  </button>
                </>
              ) : (
                <>
                  <button
                    className={`btn btn-outline-secondary ${selectedStatusFilter === "Available" ? "active" : ""
                      }`}
                    onClick={() => {
                      setSelectedStatusFilter("Available");
                      setCurrentPage(1);
                    }}
                  >
                    Available
                  </button>
                  <button
                    className={`btn btn-outline-secondary ${selectedStatusFilter === "Low Stock" ? "active" : ""
                      }`}
                    onClick={() => {
                      setSelectedStatusFilter("Low Stock");
                      setCurrentPage(1);
                    }}
                  >
                    Low Stock
                  </button>
                  <button
                    className={`btn btn-outline-secondary ${selectedStatusFilter === "Out of Stock" ? "active" : ""
                      }`}
                    onClick={() => {
                      setSelectedStatusFilter("Out of Stock");
                      setCurrentPage(1);
                    }}
                  >
                    Out of Stock
                  </button>

                </>
              )}
            </div>
          )}

          {activeTab === "appointment" && (
            <div className="input-group input-group-sm w-25">
              <span className="input-group-text bg-white text-dark border-0">
                Doctor:
              </span>
              <select
                className="form-select"
                value={doctorFilter}
                onChange={(e) => {
                  setDoctorFilter(e.target.value);
                  setCurrentPage(1);
                }}
                style={{ maxWidth: "150px" }}
              >
                <option value="All">All</option>
                {doctorListData.map((doctor) => (
                  <option key={doctor.doctor_id} value={doctor.doctor_name}>
                    {doctor.doctor_id} - {doctor.doctor_name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="d-flex align-items-center flex-wrap gap-1 mt-1 mt-md-0">
            {(activeTab === "billing" ||
              activeTab === "attendance" ||
              activeTab === "expenses" ||
              activeTab === "appointment" ||
              activeTab === "maintenance" ||
              activeTab === "pharmacy" ||
              activeTab === "overallBilling") && (
                <>
                  <div className="d-flex flex-column me-2">
                    <label
                      htmlFor="fromDate"
                      className="form-label mb-1 text-white small"
                    >
                      From Date:
                    </label>
                    <input
                      id="fromDate"
                      type="date"
                      className="form-control form-control-sm"
                      value={fromDate}
                      onChange={(e) => {
                        setFromDate(e.target.value);
                        setCurrentPage(1);
                      }}
                      style={{ maxWidth: "150px" }}
                    />
                  </div>

                  <div className="d-flex flex-column me-2">
                    <label
                      htmlFor="toDate"
                      className="form-label mb-1 text-white small"
                    >
                      To Date:
                    </label>
                    <input
                      id="toDate"
                      type="date"
                      className="form-control form-control-sm"
                      value={toDate}
                      onChange={(e) => {
                        setToDate(e.target.value);
                        setCurrentPage(1);
                      }}
                      style={{ maxWidth: "150px" }}
                    />
                  </div>
                </>
              )}

            {(activeTab === "patients" ||
              activeTab === "billing" ||
              activeTab === "appointment" ||
              activeTab === "pharmacy") && (
                <div className="input-group input-group-sm w-auto">
                  <span className="input-group-text bg-white text-dark border-0">
                    {activeTab === "patients"
                      ? "Patient Type"
                      : activeTab === "pharmacy"
                        ? "Category"
                        : "Department"}:
                  </span>
                  <select
                    className="form-select"
                    value={categoryFilter}
                    onChange={(e) => {
                      setCategoryFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                    style={{ maxWidth: "150px" }}
                  >
                    <option value="All">All</option>
                    {getCategoryOptions().map(({ value, label }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

            {activeTab === "billing" && (
              <div className="input-group input-group-sm w-auto">
                <span className="input-group-text bg-white text-dark border-0">
                  Payment Type:
                </span>
                <select
                  className="form-select"
                  value={paymentFilter}
                  onChange={(e) => {
                    setPaymentFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  style={{ maxWidth: "150px" }}
                >
                  <option value="All">All</option>
                  {[...new Set(billingManagementData.map((item) => item.payment_type?.toLowerCase()).filter(Boolean))].map(
                    (type) => (
                      <option key={type} value={type}>
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </option>
                    )
                  )}
                </select>
              </div>
            )}

            {activeTab === "billing" && (
              <div className="input-group input-group-sm w-auto">
                <span className="input-group-text bg-white text-dark border-0">
                  Service Charge:
                </span>
                <select
                  className="form-select"
                  value={serviceFilter}
                  onChange={(e) => {
                    setServiceFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  style={{ maxWidth: "150px" }}
                >
                  <option value="All">All</option>
                  {getServiceOptions().map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {activeTab === "attendance" && (
              <div className="input-group input-group-sm w-auto">
                <span className="input-group-text bg-white text-dark border-0">
                  Staff:
                </span>
                <select
                  className="form-select"
                  value={categoryFilter}
                  onChange={(e) => {
                    setCategoryFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  style={{ maxWidth: "150px" }}
                >
                  <option value="All">All</option>
                  {staffDetailsData.map((staff) => (
                    <option key={staff.staff_id} value={staff.staff_id}>
                      {staff.staff_id} -{" "}
                      {`${staff.first_name || ""} ${staff.last_name || ""}`.trim()}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        <div className="card-body">
          <div className="row mb-4 align-items-center crm-table-toolbar">
            <div className="col-md-3">
              <div className="input-group">
                <span className="input-group-text">
                  <i className="fas fa-table"></i>
                </span>
                <select
                  className="form-select"
                  value={
                    itemsPerPage === Number.MAX_SAFE_INTEGER
                      ? "All"
                      : itemsPerPage
                  }
                  onChange={(e) => {
                    setItemsPerPage(
                      e.target.value === "All"
                        ? Number.MAX_SAFE_INTEGER
                        : Number(e.target.value)
                    );
                    setCurrentPage(1);
                  }}
                >
                  <option value="All">Show All</option>
                  {[5, 10, 20, 50, 100, 200, 500, 1000].map((n) => (
                    <option key={n} value={n}>
                      Show {n}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="col-md-1"></div>
            <div className="col-md-5 mb-2">
              <div className="d-flex flex-wrap gap-2">
                <button
                  className="btn btn-success btn-sm d-flex align-items-center gap-1"
                  onClick={downloadPDF}
                >
                  <FileText size={16} />
                  PDF
                </button>
                <button
                  className="btn btn-success btn-sm d-flex align-items-center gap-1"
                  onClick={downloadExcel}
                >
                  <FileSpreadsheet size={16} />
                  Excel
                </button>
                <button
                  className="btn btn-success btn-sm d-flex align-items-center gap-1"
                  onClick={downloadCSV}
                >
                  <Download size={16} />
                  CSV
                </button>
                <button
                  className="btn btn-success btn-sm d-flex align-items-center gap-1"
                  onClick={handlePrint}
                >
                  <Printer size={16} />
                  Print
                </button>
              </div>
            </div>

            <div className="col-md-3 mb-2">
              <div className="position-relative">
                <input
                  type="text"
                  className="form-control form-control-sm pe-5"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                />
                <Search
                  size={16}
                  className="position-absolute text-muted"
                  style={{
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                />
              </div>
            </div>
          </div>

          {renderTable()}
          {renderPagination()}
        </div>
      </div>
    </div>
  );
};

export default Reports;